// "Who's responsible, what do they owe, and where does it stand" for one
// incident -- in one place. Records which driver(s) are responsible and how
// the excess is split (fixed amount or percentage, validated to add up
// exactly), creates each driver's repayment deduction and the excess-fund
// entry, restarts the insurance cycle where the setting says to, and shows
// the full trail afterwards: repairs (parts / labour), evidence, each
// driver's share, what they've paid and what's left.
//
// Works the same for an insurance claim and for a repair handled with no
// claim: nothing here depends on a claim existing. All the writes go through
// one atomic database function (confirm_incident_excess), so a failure never
// leaves a half-recorded charge.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { Loader2, Plus, Trash2, Undo2, Wrench, Link2, Unlink } from "lucide-react";
import type { InsuranceIncident } from "@/lib/incidentReports";
import { money, num, round2 } from "@/lib/driverDeductions";
import { calculateFundStatus, loadDriverFundLedger, loadInsuranceExcessSettings } from "@/lib/insuranceExcess";
import {
  addRepairLine,
  computeShares,
  confirmIncidentExcess,
  evenSplit,
  linkRepairRecord,
  loadCostTrail,
  loadLinkableCharges,
  loadResetClassifications,
  restartsCycle,
  voidIncidentResponsibilities,
  type CostTrail,
  type LinkableCharge,
  type SplitMode,
} from "@/lib/incidentResponsibility";

interface DriverChoice {
  driver_name: string;
  insurance_start_date: string | null;
  insurance_last_claim_date: string | null;
}

interface Props {
  incident: InsuranceIncident;
  drivers: DriverChoice[];
  canEdit: boolean;
  /** Plain-English label for the deduction, e.g. "At fault -- Single vehicle on 19 Aug 2026". */
  defaultDescription: string;
  /** Plain-English name of the saved fault classification, for the restart note. */
  classificationLabel: string | null;
  onChanged: () => void;
}

interface Row {
  driverName: string;
  value: string;
  weekly: string;
  /** An existing deduction to attach instead of creating a new one (no-excess mode only). */
  link: string;
}

const CHARGEABLE = ["at_fault", "section2_with_repair_cost"];
const selectClass =
  "dark-input flex h-9 w-full rounded-md border px-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

const STATUS_STYLE: Record<string, string> = {
  active: "border-green-500/50 text-green-500",
  adjusted: "border-yellow-500/50 text-yellow-500",
  paid: "border-blue-400/50 text-blue-400",
  cancelled: "border-red-400/50 text-red-400",
};

const IncidentResponsibilityPanel: React.FC<Props> = ({ incident, drivers, canEdit, defaultDescription, classificationLabel, onChanged }) => {
  const [trail, setTrail] = useState<CostTrail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetList, setResetList] = useState<string[]>(["at_fault"]);
  const [linkable, setLinkable] = useState<LinkableCharge[]>([]);
  const [suggested, setSuggested] = useState<{ amount: number; note: string } | null>(null);
  const [defaultWeekly, setDefaultWeekly] = useState(15);

  const [excessApplies, setExcessApplies] = useState(CHARGEABLE.includes(incident.fault_classification ?? ""));
  const [excessAmount, setExcessAmount] = useState("");
  const [mode, setMode] = useState<SplitMode>("percent");
  const [rows, setRows] = useState<Row[]>([]);

  const [linkId, setLinkId] = useState("");
  const [showAddRepair, setShowAddRepair] = useState(false);
  const [repair, setRepair] = useState({ description: "", cost: "", isLabour: false, supplier: "", date: new Date().toISOString().slice(0, 10) });

  const confirmed = !!incident.responsibility_confirmed_at;

  const refreshTrail = useCallback(async () => {
    setTrail(await loadCostTrail(incident.id, incident.vehicle_number));
  }, [incident.id, incident.vehicle_number]);

  // Start each incident fresh: the incident driver carrying 100%.
  useEffect(() => {
    setExcessApplies(CHARGEABLE.includes(incident.fault_classification ?? ""));
    setMode("percent");
    setRows(incident.driver_name ? [{ driverName: incident.driver_name, value: "100", weekly: "", link: "" }] : []);
    setExcessAmount("");
    setSuggested(null);
    setLinkId("");
    setShowAddRepair(false);
  }, [incident.id, incident.driver_name, incident.fault_classification]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [t, reset, settings, charges] = await Promise.all([
          loadCostTrail(incident.id, incident.vehicle_number),
          loadResetClassifications(),
          loadInsuranceExcessSettings(),
          loadLinkableCharges(),
        ]);
        if (cancelled) return;
        setTrail(t);
        setResetList(reset);
        setLinkable(charges);
        setDefaultWeekly(settings.weeklyContribution);

        // Suggest the amount the old flow proposed: the fund's claimable tier for an
        // at-fault claim, or the actual repair cost for Section 2 + repair cost.
        let s: { amount: number; note: string } | null = null;
        if (incident.fault_classification === "section2_with_repair_cost" && incident.repair_cost) {
          s = { amount: num(incident.repair_cost), note: "the recorded repair cost" };
        } else if (incident.fault_classification === "at_fault" && incident.driver_name) {
          const driver = drivers.find((d) => d.driver_name === incident.driver_name);
          const ledger = await loadDriverFundLedger(incident.driver_name);
          const status = calculateFundStatus(
            { insurance_start_date: driver?.insurance_start_date ?? null, insurance_last_claim_date: driver?.insurance_last_claim_date ?? null },
            ledger,
            settings
          );
          if (status.claimableTier > 0) {
            s = { amount: status.claimableTier, note: `${incident.driver_name}'s claimable amount from the excess fund (${status.monthsContributing ?? 0} months contributing)` };
          } else {
            s = { amount: 0, note: `${incident.driver_name} hasn't reached the minimum contribution period, so nothing is claimable from the fund yet` };
          }
        }
        if (cancelled) return;
        setSuggested(s);
        if (s && s.amount > 0) setExcessAmount((prev) => prev || String(s!.amount));
      } catch (e) {
        if (!cancelled) toast({ title: "Couldn't load the cost trail", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident.id, incident.responsibility_confirmed_at]);

  const total = parseFloat(excessAmount) || 0;
  const entries = useMemo(
    () => rows.map((r) => ({ driverName: r.driverName, value: parseFloat(r.value) || 0 })),
    [rows]
  );
  const split = useMemo(() => (excessApplies ? computeShares(total, mode, entries) : null), [excessApplies, total, mode, entries]);

  const weeklyProblem = excessApplies && split && !split.error
    ? rows.some((r, i) => split.shares[i] > 0 && !(parseFloat(r.weekly || String(defaultWeekly)) > 0))
    : false;

  const noExcessProblem = !excessApplies
    ? rows.length === 0
      ? "Choose at least one responsible driver"
      : rows.some((r) => !r.driverName)
      ? "Choose a driver for every line"
      : new Set(rows.map((r) => r.driverName)).size !== rows.length
      ? "A driver can only be listed once"
      : null
    : null;

  const problem = excessApplies ? split?.error ?? (weeklyProblem ? "Enter a weekly deduction for each driver" : null) : noExcessProblem;
  const restarts = restartsCycle(incident.fault_classification, resetList);

  const setRow = (i: number, patch: Partial<Row>) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const resplit = (nextRows: Row[], nextMode: SplitMode) => {
    const even = evenSplit(total, nextMode, nextRows.map((r) => r.driverName || `__new${Math.random()}`));
    return nextRows.map((r, i) => ({ ...r, value: String(even[i].value) }));
  };

  const addDriver = () => {
    const next = [...rows, { driverName: "", value: "0", weekly: "", link: "" }];
    setRows(next);
  };

  const changeMode = (next: SplitMode) => {
    setMode(next);
    setRows((prev) => resplit(prev, next));
  };

  const confirm = async () => {
    if (!canEdit || problem) return;
    setSaving(true);
    try {
      const result = await confirmIncidentExcess({
        incidentId: incident.id,
        excessApplies,
        excessAmount: total,
        allocations: rows.map((r) => ({
          driverName: r.driverName,
          type: excessApplies ? mode : undefined,
          value: excessApplies ? parseFloat(r.value) || 0 : undefined,
          weekly: excessApplies ? parseFloat(r.weekly || String(defaultWeekly)) || undefined : undefined,
          linkAdjustmentId: !excessApplies && r.link ? r.link : undefined,
        })),
        description: defaultDescription,
      });
      toast({
        title: "Responsibility recorded",
        description: [
          excessApplies
            ? result.allocations
                .filter((a) => a.allocated > 0)
                .map((a) => `${a.driver_name}: ${money(a.allocated)} at ${money(a.weekly ?? 0)}/week`)
                .join("; ")
            : `${rows.length} driver${rows.length === 1 ? "" : "s"} recorded as responsible, no excess.`,
          result.restart_cycle ? "Their insurance cycle has been restarted." : "Their insurance cycle was not restarted (per the Insurance Excess setting).",
        ].join(" "),
      });
      await refreshTrail();
      onChanged();
    } catch (e) {
      toast({ title: "Couldn't record that", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const undo = async () => {
    const note = window.prompt("Why is this being undone? (required -- it's saved on the record)");
    if (!note || !note.trim()) return;
    setSaving(true);
    try {
      const r = await voidIncidentResponsibilities(incident.id, note.trim());
      toast({
        title: "Responsibility undone",
        description:
          `${r.charges_cancelled} unpaid charge${r.charges_cancelled === 1 ? "" : "s"} cancelled.` +
          (r.fund_entries_kept_because_already_collected > 0
            ? ` ${r.fund_entries_kept_because_already_collected} excess-fund entr${r.fund_entries_kept_because_already_collected === 1 ? "y was" : "ies were"} left as is because money has already been collected against it -- reverse those in the Deductions Report.`
            : ""),
      });
      await refreshTrail();
      onChanged();
    } catch (e) {
      toast({ title: "Couldn't undo that", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const doLink = async () => {
    if (!linkId) return;
    try {
      await linkRepairRecord(linkId, incident.id);
      setLinkId("");
      await refreshTrail();
    } catch (e) {
      toast({ title: "Couldn't link that", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
  };

  const doUnlink = async (id: string) => {
    try {
      await linkRepairRecord(id, null);
      await refreshTrail();
    } catch (e) {
      toast({ title: "Couldn't unlink that", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
  };

  const saveRepair = async () => {
    const cost = parseFloat(repair.cost);
    if (!incident.vehicle_number) {
      toast({ title: "No vehicle on this incident", description: "Pick the vehicle in Incident Details first.", variant: "destructive" });
      return;
    }
    if (!repair.description.trim() || !Number.isFinite(cost) || cost < 0) {
      toast({ title: "Describe the repair and enter its cost", variant: "destructive" });
      return;
    }
    try {
      await addRepairLine({
        incidentId: incident.id,
        vehicleNumber: incident.vehicle_number,
        date: repair.date,
        description: repair.description.trim(),
        cost,
        isLabour: repair.isLabour,
        supplier: repair.supplier.trim(),
      });
      setRepair((r) => ({ ...r, description: "", cost: "", supplier: "", isLabour: false }));
      setShowAddRepair(false);
      await refreshTrail();
    } catch (e) {
      toast({ title: "Couldn't save the repair line", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
  };

  const driverOptions = (current: string) =>
    drivers.filter((d) => d.driver_name === current || !rows.some((r) => r.driverName === d.driver_name));

  return (
    <div className="border-t border-border pt-3 space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold">Responsibility, Excess &amp; Cost Trail</Label>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </div>

      {/* ---------- Who is responsible ---------- */}
      {confirmed && trail ? (
        <div className="rounded-lg border border-border bg-background/40 p-3 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="text-sm">
              <span className="font-medium">{incident.excess_applies ? `Excess ${money(num(incident.excess_amount))}` : "No excess applies"}</span>
              <span className="text-muted-foreground"> · {classificationLabel ?? "unclassified"}</span>
            </div>
            {canEdit && (
              <Button size="sm" variant="outline" onClick={undo} disabled={saving}>
                <Undo2 className="w-3.5 h-3.5 mr-1.5" />
                Undo
              </Button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-1 pr-3">Driver</th>
                  <th className="py-1 pr-3 text-right">Share</th>
                  <th className="py-1 pr-3">Split</th>
                  <th className="py-1 pr-3 text-right">Weekly</th>
                  <th className="py-1 pr-3 text-right">Paid</th>
                  <th className="py-1 pr-3 text-right">Still owed</th>
                  <th className="py-1">Status</th>
                </tr>
              </thead>
              <tbody>
                {trail.shares.map((s) => (
                  <tr key={s.id} className="border-b border-border/40">
                    <td className="py-1 pr-3">
                      {s.driver_name}
                      {s.restarted_cycle && <span className="ml-1 text-[10px] text-yellow-500">cycle restarted</span>}
                    </td>
                    <td className="py-1 pr-3 text-right">{money(num(s.allocated_amount))}</td>
                    <td className="py-1 pr-3 text-xs text-muted-foreground">
                      {s.allocation_type === "percent" ? `${num(s.allocation_value)}%` : s.allocation_type === "fixed" ? "fixed" : "—"}
                    </td>
                    <td className="py-1 pr-3 text-right">{s.weekly_amount !== null ? money(num(s.weekly_amount)) : "—"}</td>
                    <td className="py-1 pr-3 text-right">{money(s.paid)}</td>
                    <td className="py-1 pr-3 text-right font-medium">{s.remaining === null ? "—" : money(s.remaining)}</td>
                    <td className="py-1">
                      {s.status ? (
                        <Badge variant="outline" className={`text-[10px] ${STATUS_STYLE[s.status] ?? ""}`}>{s.status}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">no charge</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">
            Every payment is in the Payout Report → Deductions Report. This stays on each driver's claim history even if they're later deactivated.
          </p>
        </div>
      ) : canEdit ? (
        <div className="rounded-lg border border-border bg-background/40 p-3 space-y-3">
          {!incident.fault_classification && (
            <p className="text-xs text-amber-400">Save a fault classification above first -- it decides whether this restarts a driver's insurance cycle.</p>
          )}

          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={excessApplies} onCheckedChange={(v) => setExcessApplies(Boolean(v))} />
            An excess applies to this incident
          </label>

          {excessApplies && (
            <div className="space-y-1">
              <Label className="text-xs">Excess amount ($)</Label>
              <Input type="number" min="0" step="0.01" value={excessAmount} onChange={(e) => setExcessAmount(e.target.value)} className="w-40" />
              {suggested && (
                <p className="text-xs text-muted-foreground">
                  Suggested {money(suggested.amount)} -- {suggested.note}.
                  {suggested.amount > 0 && num(excessAmount) !== suggested.amount && (
                    <button type="button" className="ml-1 underline" onClick={() => setExcessAmount(String(suggested.amount))}>Use it</button>
                  )}
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label className="text-xs">Responsible driver{rows.length === 1 ? "" : "s"}</Label>
              {excessApplies && (
                <div className="flex rounded-md border border-border overflow-hidden text-xs">
                  {(["percent", "fixed"] as SplitMode[]).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => changeMode(m)}
                      className={`px-2.5 py-1 ${mode === m ? "bg-yellow-500 text-black" : "text-muted-foreground hover:bg-muted"}`}
                    >
                      {m === "percent" ? "Split by %" : "Split by $"}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <div className={excessApplies ? "col-span-5" : "col-span-10"}>
                  {i === 0 && <Label className="text-[10px] text-muted-foreground">Driver</Label>}
                  <select className={selectClass} value={r.driverName} onChange={(e) => setRow(i, { driverName: e.target.value })}>
                    <option value="">Choose a driver...</option>
                    {driverOptions(r.driverName).map((d) => (
                      <option key={d.driver_name} value={d.driver_name}>{d.driver_name}</option>
                    ))}
                  </select>
                </div>
                {excessApplies && (
                  <>
                    <div className="col-span-3">
                      {i === 0 && <Label className="text-[10px] text-muted-foreground">{mode === "percent" ? "Percent (%)" : "Amount ($)"}</Label>}
                      <Input type="number" min="0" step="0.01" value={r.value} onChange={(e) => setRow(i, { value: e.target.value })} className="h-9" />
                    </div>
                    <div className="col-span-3">
                      {i === 0 && <Label className="text-[10px] text-muted-foreground">Weekly ($)</Label>}
                      <Input type="number" min="0" step="0.01" value={r.weekly} placeholder={String(defaultWeekly)} onChange={(e) => setRow(i, { weekly: e.target.value })} className="h-9" />
                    </div>
                  </>
                )}
                <div className="col-span-1 flex justify-end">
                  <Button type="button" variant="ghost" size="sm" className="h-9 px-2" onClick={() => setRows((prev) => prev.filter((_, idx) => idx !== i))} title="Remove">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                {!excessApplies && r.driverName && linkable.some((c) => c.driver_name === r.driverName) && (
                  <div className="col-span-12 space-y-1">
                    <Label className="text-[10px] text-muted-foreground">
                      Already charging them for this damage? Link that deduction instead of creating a new one (optional)
                    </Label>
                    <select className={selectClass} value={r.link} onChange={(e) => setRow(i, { link: e.target.value })}>
                      <option value="">Don't link one</option>
                      {linkable
                        .filter((c) => c.driver_name === r.driverName)
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {(c.description || "Deduction").trim()} — {money(num(c.total_amount))} total, {money(num(c.remaining_balance))} owed
                          </option>
                        ))}
                    </select>
                  </div>
                )}
              </div>
            ))}

            <Button type="button" variant="outline" size="sm" onClick={addDriver}>
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Add another responsible driver
            </Button>
          </div>

          {excessApplies && split && (
            <div className={`rounded-md border p-2 text-xs ${split.error ? "border-red-500/40 bg-red-500/10 text-red-300" : "border-green-500/40 bg-green-500/10 text-green-300"}`}>
              {split.error ? (
                <>
                  {split.error}
                  {split.unallocated !== 0 && (
                    <span> ({split.unallocated > 0 ? "still to hand out" : "over by"}: {mode === "percent" ? `${Math.abs(split.unallocated)}%` : money(Math.abs(split.unallocated))})</span>
                  )}
                </>
              ) : (
                rows.map((r, i) => `${r.driverName}: ${money(split.shares[i])}`).join("  ·  ") + `  =  ${money(round2(split.shares.reduce((s, v) => s + v, 0)))}`
              )}
            </div>
          )}
          {!excessApplies && noExcessProblem && <p className="text-xs text-red-300">{noExcessProblem}</p>}
          {weeklyProblem && <p className="text-xs text-red-300">Enter a weekly deduction for each driver.</p>}

          <p className="text-xs text-muted-foreground">
            {incident.fault_classification
              ? restarts
                ? `Because this is "${classificationLabel}", confirming restarts the insurance cycle for these drivers.`
                : `"${classificationLabel}" doesn't restart the insurance cycle (Settings → Insurance Excess). It still goes on each driver's claim history.`
              : ""}
          </p>

          <Button size="sm" className="golden-button" onClick={confirm} disabled={saving || !!problem || !incident.fault_classification}>
            {saving ? "Recording..." : excessApplies ? "Confirm Charge" : "Record Responsibility"}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Responsibility hasn't been recorded for this incident yet.</p>
      )}

      {/* ---------- Repairs & evidence ---------- */}
      {trail && (
        <div className="rounded-lg border border-border bg-background/40 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Wrench className="w-4 h-4 text-yellow-500" />
              Repairs &amp; costs
            </div>
            <span className="text-xs text-muted-foreground">
              Evidence: {trail.evidence.photos} photo{trail.evidence.photos === 1 ? "" : "s"} · {trail.evidence.documents} claim document{trail.evidence.documents === 1 ? "" : "s"} · this incident report
            </span>
          </div>

          {incident.repair_estimate != null && (
            <p className="text-xs text-muted-foreground">
              Quote / estimate on file: <span className="text-foreground">{money(num(incident.repair_estimate))}</span>
              {incident.repairer_name ? ` from ${incident.repairer_name}` : ""} (set in Fault Classification above).
            </p>
          )}

          {trail.repairs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-1 pr-3">Date</th>
                    <th className="py-1 pr-3">What</th>
                    <th className="py-1 pr-3">Supplier</th>
                    <th className="py-1 pr-3">Type</th>
                    <th className="py-1 pr-3 text-right">Cost</th>
                    <th className="py-1" />
                  </tr>
                </thead>
                <tbody>
                  {trail.repairs.map((r) => (
                    <tr key={r.id} className="border-b border-border/40">
                      <td className="py-1 pr-3 whitespace-nowrap">{r.service_date}</td>
                      <td className="py-1 pr-3">{[r.system_name, r.work_type_name].filter(Boolean).join(" — ") || "Repair"}</td>
                      <td className="py-1 pr-3 text-muted-foreground">{r.supplier ?? ""}</td>
                      <td className="py-1 pr-3">{r.is_labour ? "Labour" : "Parts"}</td>
                      <td className="py-1 pr-3 text-right">{money(num(r.cost))}</td>
                      <td className="py-1 text-right">
                        {canEdit && (
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => doUnlink(r.id)} title="Unlink from this incident">
                            <Unlink className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} className="pt-2 text-right text-xs text-muted-foreground">
                      Parts {money(trail.partsTotal)} · Labour {money(trail.labourTotal)} · Total
                    </td>
                    <td className="pt-2 pr-3 text-right font-semibold">{money(trail.repairsTotal)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No repair costs linked to this incident yet.</p>
          )}

          {canEdit && (
            <div className="space-y-2">
              {trail.unlinkedRepairs.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <select className={`${selectClass} max-w-md`} value={linkId} onChange={(e) => setLinkId(e.target.value)}>
                    <option value="">Link an existing maintenance record for #{incident.vehicle_number}...</option>
                    {trail.unlinkedRepairs.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.service_date} — {[r.system_name, r.work_type_name].filter(Boolean).join(" — ") || "Repair"} — {money(num(r.cost))}
                      </option>
                    ))}
                  </select>
                  <Button size="sm" variant="outline" onClick={doLink} disabled={!linkId}>
                    <Link2 className="w-3.5 h-3.5 mr-1.5" />
                    Link
                  </Button>
                </div>
              )}

              {showAddRepair ? (
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-3">
                    <Label className="text-[10px] text-muted-foreground">Date</Label>
                    <Input type="date" value={repair.date} onChange={(e) => setRepair((r) => ({ ...r, date: e.target.value }))} className="h-9" />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-[10px] text-muted-foreground">What was done / bought</Label>
                    <Input value={repair.description} onChange={(e) => setRepair((r) => ({ ...r, description: e.target.value }))} className="h-9" placeholder="e.g. Front door panel" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px] text-muted-foreground">Cost ($)</Label>
                    <Input type="number" min="0" step="0.01" value={repair.cost} onChange={(e) => setRepair((r) => ({ ...r, cost: e.target.value }))} className="h-9" />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[10px] text-muted-foreground">Supplier</Label>
                    <Input value={repair.supplier} onChange={(e) => setRepair((r) => ({ ...r, supplier: e.target.value }))} className="h-9" />
                  </div>
                  <label className="col-span-12 flex items-center gap-2 text-xs">
                    <Checkbox checked={repair.isLabour} onCheckedChange={(v) => setRepair((r) => ({ ...r, isLabour: Boolean(v) }))} />
                    This line is labour (otherwise parts)
                  </label>
                  <div className="col-span-12 flex gap-2">
                    <Button size="sm" onClick={saveRepair}>Save repair line</Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowAddRepair(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setShowAddRepair(true)}>
                  <Plus className="w-3.5 h-3.5 mr-1.5" />
                  Add a repair cost
                </Button>
              )}
            </div>
          )}

          {confirmed && incident.excess_applies && trail.repairsTotal > 0 && (
            <p className="text-xs text-muted-foreground">
              Repairs {money(trail.repairsTotal)} vs excess charged to drivers {money(num(incident.excess_amount))}.
            </p>
          )}
        </div>
      )}

      {trail && trail.voided.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Earlier responsibility records for this incident were undone: {trail.voided.map((v) => v.driver_name).join(", ")}. They stay on the audit trail.
        </p>
      )}
    </div>
  );
};

export default IncidentResponsibilityPanel;
